/* eslint-disable react/no-unsafe -- TODO: Convert component to functional component */
/**
 * This component is used to pop-up and dismiss small notifications. Eventually,
 * It will be used in a fixed position on the site, and tied in with the
 * notification system.  For the time being, it is relatively positioned by
 * the parent.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import {borderRadius} from "@khan/shared-styles";

import closeIcon from "@phosphor-icons/core/regular/x.svg";

const animationDuration = 0.2;

export interface SharedToastProps {
    // The content of the toast notification
    children?: React.ReactNode;
    // You can include a standardized link at the bottom of the toast
    linkText?: string;
    // The HREF for the link
    href?: string;
    // An optional test id for use in tests
    testId?: string;
    // Styles for toast container.
    style?: CSSProperties;
    // Color for close icon.
    closeIconColor?: string;
    // The toast has been closed
    onClose: () => void;
}

type Props = SharedToastProps & {
    // Time in ms when to automatically close the toast notification
    autoTimeout?: number;
    // Error toasts feature a red line across the left edge,
    // and need to be manually closed.
    isError?: boolean;
};

interface State {
    closing: boolean;
    mousingOver: boolean;
    closeAfterMouseLeave: boolean;
}

/* eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
   --
   TODO: Convert component to functional component */
class ToastBase extends React.PureComponent<Props, State> {
    timeoutId: number | null | undefined;

    state: State = {
        closing: false,
        mousingOver: false,
        closeAfterMouseLeave: false,
    };

    componentDidMount() {
        const {autoTimeout} = this.props;

        if (typeof autoTimeout === "number") {
            this.scheduleTimeout(autoTimeout);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // If we aren't adding a new timeout, we can bail early.
        // This has to be done as a discrete step to appease Flow.
        if (nextProps.autoTimeout === undefined) {
            return;
        }

        // We should register a timeout if we didn't already have one
        const shouldRegisterTimeout = this.props.autoTimeout === undefined;

        if (shouldRegisterTimeout) {
            this.scheduleTimeout(nextProps.autoTimeout);
        }
    }

    componentWillUnmount() {
        if (this.timeoutId) {
            /* eslint-disable-next-line no-restricted-syntax
               --
               TODO(jeff, WEB-1378): Use Wonder Blocks Timing API. */
            clearTimeout(this.timeoutId);
        }
    }

    scheduleTimeout(ms: number) {
        // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
        // eslint-disable-next-line no-restricted-syntax -- TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
        this.timeoutId = setTimeout(() => {
            const {mousingOver} = this.state;
            if (mousingOver) {
                this.setState({closeAfterMouseLeave: true});
            } else {
                this.handleClose();
            }
        }, ms);
    }

    handleMouseMove: () => void = () => {
        this.setState({mousingOver: true});
    };

    handleMouseLeave: () => void = () => {
        const {closeAfterMouseLeave} = this.state;

        if (closeAfterMouseLeave) {
            this.handleClose();
        }

        this.setState({mousingOver: false});
    };

    handleClose: () => void = () => {
        const {onClose} = this.props;

        this.setState({closing: true});

        if (onClose) {
            /* eslint-disable-next-line no-restricted-syntax
               --
               TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
               Dangling timer could fire after component unmounts!
             */
            setTimeout(() => onClose(), animationDuration * 1000);
        }
    };

    render(): React.ReactNode {
        const {
            children,
            href,
            linkText,
            testId,
            style,
            closeIconColor,
            isError,
        } = this.props;
        const {closing} = this.state;

        const animation = closing ? styles.fadeOut : styles.fadeIn;

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
            <div
                onMouseMove={this.handleMouseMove}
                onMouseLeave={this.handleMouseLeave}
                className={css(
                    styles.toast,
                    animation,
                    isError && styles.isError,
                    style,
                )}
                data-testid={testId}
            >
                <div className={css(styles.content)}>
                    <div>{children}</div>
                </div>
                {linkText && href && (
                    <Link href={href} testId="toast-link" style={styles.link}>
                        {linkText}
                    </Link>
                )}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- TODO: Address a11y error */}
                <span
                    className={css(styles.close)}
                    onClick={this.handleClose}
                    role="button"
                    aria-label={t`Close`}
                >
                    <PhosphorIcon
                        icon={closeIcon}
                        size="small"
                        color={
                            closeIconColor ||
                            semanticColor.core.foreground.neutral.default
                        }
                    />
                </span>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    toast: {
        maxWidth: "80vw",
        minBlockSize: 32,
        padding: 16,
        paddingInlineStart: 24,
        paddingInlineEnd: 36,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        border: "solid 1px rgba(0, 0, 0, 0.1)",
        borderRadius,
        boxSizing: "border-box",
        fontSize: 12,
        color: semanticColor.core.foreground.neutral.strong,
        lineHeight: "normal",
        position: "relative",
    },
    isError: {
        ":before": {
            content: '""',
            position: "absolute",
            top: -1,
            left: -1,
            bottom: -1,
            width: 8,
            backgroundColor: semanticColor.core.background.critical.default,
            borderRadius: `${borderRadius}px 0 0 ${borderRadius}px`,
        },
    },
    content: {
        display: "flex",
    },
    close: {
        position: "absolute",
        insetInlineEnd: 10,
        top: 10,
        cursor: "pointer",
    },
    link: {
        paddingBlockStart: 3,
        fontWeight: "bold",
    },

    fadeIn: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { opacity: number; }; "100%": { opacity: number; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                opacity: 0,
            },
            "100%": {
                opacity: 1,
            },
        },
        animationDuration: `${animationDuration}s`,
        animationIterationCount: 1,
    },
    fadeOut: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { opacity: number; }; "100%": { opacity: number; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                opacity: 1,
            },
            "100%": {
                opacity: 0,
            },
        },
        animationDuration: `${animationDuration}s`,
        animationIterationCount: 1,
        animationFillMode: "forwards",
    },
});

export default ToastBase;
